// ============================================================
// Native — WebView <-> React Native RPC Protocol
// ============================================================
//
// Two channels:
//   1. RPC      — web.invoke(method) → native handles → response (Promise)
//   2. Notify   — fire-and-forget event on either side (web.notify / on)
//
// Envelope formats are stable across versions; new methods/events are
// added by extending NativeMethods or NativeNotifications.
// ============================================================

export interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface DeviceInfoPayload {
  platform: 'ios' | 'android';
  deviceId: string;
  model: string;
  appVersion: string;
  osVersion: string;
  locale: string;
  theme: 'light' | 'dark';
  /** Native safe-area insets, also surfaced as `--safe-area-*` CSS vars. */
  safeArea?: SafeAreaInsets;
}

export interface StorageSyncPayload {
  action: 'set' | 'remove' | 'clear';
  key?: string;
  value?: string;
}

export interface ShowAlertParams {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

export interface ShowAlertResult {
  confirmed: boolean;
}

// ------------------------------------------------------------
// UI / Feedback
// ------------------------------------------------------------

export type HapticType =
  | 'light'
  | 'medium'
  | 'heavy'
  | 'success'
  | 'warning'
  | 'error';

export interface ShowToastParams {
  text: string;
  duration?: 'short' | 'long';
  type?: 'success' | 'error' | 'info';
}

export interface ShareParams {
  text?: string;
  url?: string;
  title?: string;
}

export interface ShareResult {
  shared: boolean;
}

// ------------------------------------------------------------
// System / Network
// ------------------------------------------------------------

export interface NetworkStatus {
  online: boolean;
  connectionType: 'wifi' | 'cellular' | 'none' | 'unknown';
}

export interface AppVersion {
  version: string;
  buildNumber: string;
}

// ------------------------------------------------------------
// Deep linking
// ------------------------------------------------------------

export interface DeepLinkPayload {
  /** Full URL received by the native shell. */
  url: string;
  /**
   * `cold-start` when the app was launched by this link.
   * `background` when the app was already running and brought to
   * the foreground by the link.
   */
  source: 'cold-start' | 'background';
}

// ------------------------------------------------------------
// Permissions
// ------------------------------------------------------------

export type PermissionKind = 'camera' | 'photo' | 'notification';

export type PermissionStatus = 'granted' | 'denied' | 'blocked' | 'unavailable';

export interface PermissionResult {
  status: PermissionStatus;
}

// ------------------------------------------------------------
// Push notifications
// ------------------------------------------------------------

export interface PushTokenResult {
  /** FCM/APNs token. `null` until the OS mints one (e.g. iOS simulator). */
  token: string | null;
  platform: 'ios' | 'android';
}

/**
 * How a push surfaced to the app:
 *   - `foreground`      arrived while the app was active (no tap).
 *   - `background-tap`  the user tapped a notification that woke the app.
 *   - `cold-start-tap`  the user tapped a notification that launched the app.
 */
export type PushReceivedSource =
  | 'foreground'
  | 'background-tap'
  | 'cold-start-tap';

export interface PushReceivedPayload {
  title?: string;
  body?: string;
  /** Server-defined data (FCM data payload), coerced to string values. */
  data?: Record<string, string>;
  source: PushReceivedSource;
}

export interface PushTokenChangedPayload {
  token: string;
  platform: 'ios' | 'android';
}

// ------------------------------------------------------------
// RPC: web → native, awaits response
// ------------------------------------------------------------

export interface NativeMethods {
  // -- Existing: device & navigation & dialogs
  getDeviceInfo: { params: void; result: DeviceInfoPayload };
  showAlert: { params: ShowAlertParams; result: ShowAlertResult };
  openInAppWeb: { params: { url: string; title?: string }; result: void };
  openExternalUrl: { params: { url: string }; result: void };
  dismissKeyboard: { params: void; result: void };
  setStatusBarStyle: {
    params: { style: 'light-content' | 'dark-content' };
    result: void;
  };

  // -- Tier 1: UI/UX basics
  haptic: { params: { type: HapticType }; result: void };
  showToast: { params: ShowToastParams; result: void };
  share: { params: ShareParams; result: ShareResult };
  openAppSettings: { params: void; result: void };

  // -- Tier 2: system / network
  getNetworkStatus: { params: void; result: NetworkStatus };
  getAppVersion: { params: void; result: AppVersion };

  // -- Tier 3: permissions
  requestPermission: {
    params: { kind: PermissionKind };
    result: PermissionResult;
  };
  getPermissionStatus: {
    params: { kind: PermissionKind };
    result: PermissionResult;
  };

  // -- Tier 4: push notifications
  getPushToken: { params: void; result: PushTokenResult };
}

export type NativeMethod = keyof NativeMethods;
export type NativeParams<M extends NativeMethod> = NativeMethods[M]['params'];
export type NativeResult<M extends NativeMethod> = NativeMethods[M]['result'];

// ------------------------------------------------------------
// Notifications: fire-and-forget, both directions
// ------------------------------------------------------------

export interface NativeNotifications {
  themeChanged: { theme: 'light' | 'dark' };
  languageChanged: { language: string };
  appStateChanged: { state: 'active' | 'background' | 'inactive' };
  keyboardStateChanged: { visible: boolean; height: number };
  storageSync: StorageSyncPayload;
  reloadApp: void;
  networkStatusChanged: NetworkStatus;
  deepLink: DeepLinkPayload;
  // native → web only: a push arrived or was tapped.
  pushReceived: PushReceivedPayload;
  // native → web only: the push token was issued or rotated.
  pushTokenChanged: PushTokenChangedPayload;
}

export type NativeEvent = keyof NativeNotifications;
export type NativePayload<E extends NativeEvent> = NativeNotifications[E];

// ------------------------------------------------------------
// Wire envelope (what crosses the WebView boundary)
// ------------------------------------------------------------

export interface NativeRequestEnvelope {
  kind: 'request';
  id: string;
  method: NativeMethod;
  params: unknown;
}

export type NativeResponseEnvelope =
  | {
      kind: 'response';
      id: string;
      ok: true;
      result: unknown;
    }
  | {
      kind: 'response';
      id: string;
      ok: false;
      error: string;
    };

export interface NativeNotificationEnvelope {
  kind: 'notification';
  event: NativeEvent;
  payload: unknown;
}

export type NativeEnvelope =
  | NativeRequestEnvelope
  | NativeResponseEnvelope
  | NativeNotificationEnvelope;

// ------------------------------------------------------------
// Type guards
// ------------------------------------------------------------

export const isNativeEnvelope = (data: unknown): data is NativeEnvelope => {
  if (typeof data !== 'object' || data === null) return false;
  const rec = data as Record<string, unknown>;
  return (
    rec.kind === 'request' ||
    rec.kind === 'response' ||
    rec.kind === 'notification'
  );
};

export const isNativeRequest = (
  env: NativeEnvelope,
): env is NativeRequestEnvelope => env.kind === 'request';

export const isNativeResponse = (
  env: NativeEnvelope,
): env is NativeResponseEnvelope => env.kind === 'response';

export const isNativeNotification = (
  env: NativeEnvelope,
): env is NativeNotificationEnvelope => env.kind === 'notification';
